<template>
  <div :class="LAYOUT_CLASSES[layout]">
    <span :class="classes.label">{{ label }}</span>
    <span v-if="value" :class="classes.value">{{ value }}</span>
  </div>
</template>

<script setup lang="ts">
/**
 * A "label: value" line — the numpad's context lines, the receipt-processing
 * card's change. Callers say how loud the line reads (`emphasis`) and how it
 * is laid out (`layout`); the type ramp steps for each level live here, so a
 * louder line is a setting, never a per-screen font override.
 */
import { computed } from 'vue';
import type { LabeledValueEmphasis, LabeledValueLayout } from '@/types/components';

const props = withDefaults(defineProps<{
  /** May be empty: a numpad line has nothing to show until there is an amount. */
  label?: string
  value?: string
  emphasis?: LabeledValueEmphasis
  layout?: LabeledValueLayout
}>(), {
  label: '',
  value: undefined,
  emphasis: 'default',
  layout: 'row',
});

const EMPHASIS_CLASSES: Record<LabeledValueEmphasis, { label: string, value: string }> = {
  default: { label: '', value: 'text-identifier' },
  prominent: { label: 'text-display', value: 'text-display' },
  hero: { label: 'text-panel-title text-muted', value: 'text-display-lg' },
};

const LAYOUT_CLASSES: Record<LabeledValueLayout, string> = {
  row: 'flex items-center justify-between gap-3',
  stacked: 'flex flex-col items-center gap-1 text-center',
};

const classes = computed(() => EMPHASIS_CLASSES[props.emphasis]);
</script>
